import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { projectsData } from '@/data/projectsData';
import { usePageMeta } from '@/hooks/usePageMeta';

const filters = [
  { id: 'all', label: 'All mandates' },
  { id: 'plotted', label: 'Plotted' },
  { id: 'villas', label: 'Villas' },
  { id: 'residential', label: 'Residential' },
];

const matchesFilter = (project, filter) => {
  const type = project.type.toLowerCase();
  if (filter === 'all') return true;
  if (filter === 'plotted') return type.includes('plot') || type.includes('land');
  if (filter === 'villas') return type.includes('villa');
  return type.includes('residence') || type.includes('residential');
};

const ProjectsListing = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  usePageMeta({
    title: '11 Real Estate Mandates | Next Leap Inc',
    description: 'Explore plotted developments, villas, and residential mandates across India curated by Next Leap Inc.',
  });

  const filteredProjects = useMemo(() => {
    const search = query.trim().toLowerCase();
    return projectsData.filter((project) => {
      const text = `${project.name} ${project.location} ${project.type} ${project.tagline}`.toLowerCase();
      return matchesFilter(project, filter) && (!search || text.includes(search));
    });
  }, [filter, query]);

  return (
    <main className="blueprint-grid min-h-screen bg-[#F5F5F5] pt-32" data-testid="projects-listing-page">
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <div className="mb-5 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Mandate catalogue</div>
            <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight text-[#0B1F3B] sm:text-5xl lg:text-6xl">Eleven Indian growth stories, curated as investment-grade mandates.</h1>
          </div>
          <p className="max-w-2xl text-base leading-relaxed text-[#0B1F3B]/70 lg:justify-self-end">From Khopoli’s airport corridor to Goa’s shoreline and Vrindavan’s spiritual economy, every project is selected for a specific wealth thesis.</p>
        </motion.div>

        <div className="mb-8 grid gap-4 rounded-[2rem] border border-[#0B1F3B]/10 bg-white/80 p-4 shadow-sm backdrop-blur-xl lg:grid-cols-[1fr_auto]" data-testid="projects-toolbar">
          <label className="relative block">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#0B1F3B]/40" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by project, location, or thesis" className="h-[52px] rounded-full border-[#0B1F3B]/10 bg-[#F5F5F5] pl-12" data-testid="projects-search-input" />
          </label>
          <div className="flex flex-wrap gap-2">
            {filters.map((item) => (
              <button key={item.id} onClick={() => setFilter(item.id)} data-testid={`projects-filter-${item.id}`} className={`rounded-full px-5 py-3 font-tech text-[11px] uppercase tracking-[0.16em] transition-all ${filter === item.id ? 'bg-[#0B1F3B] text-white' : 'bg-[#F5F5F5] text-[#0B1F3B]/60 hover:bg-[#EFE6D8] hover:text-[#0B1F3B]'}`}>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <div className="rounded-[2rem] border border-dashed border-[#0B1F3B]/20 bg-white p-16 text-center" data-testid="projects-empty-state">
            <h2 className="font-display text-3xl font-semibold">No mandate matches that brief.</h2>
            <p className="mt-3 text-[#0B1F3B]/60">Try a different search or contact the team for an off-catalogue opportunity.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
            {filteredProjects.map((project, index) => (
              <motion.article key={project.id} initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.18 }} transition={{ duration: 0.6, delay: (index % 3) * 0.07 }} className={`group overflow-hidden rounded-[2rem] border border-[#0B1F3B]/10 bg-white shadow-sm transition-all duration-500 hover:-translate-y-2 hover:border-[#C5A880]/50 hover:shadow-[0_28px_80px_rgba(6,16,30,0.14)] ${project.featured ? 'md:col-span-1' : ''}`} data-testid={`project-card-${project.id}`}>
                <Link to={`/projects/${project.id}`} className="block">
                  <div className="relative h-72 overflow-hidden bg-[#0B1F3B]">
                    <img src={project.image} alt={project.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#06101E]/70 via-transparent to-transparent" />
                    <div className="absolute left-5 top-5 rounded-full bg-[#F5F5F5]/90 px-3 py-2 font-tech text-[10px] uppercase tracking-[0.16em] text-[#0B1F3B]">#{String(index + 1).padStart(2, '0')}</div>
                    <div className="absolute bottom-5 right-5 rounded-full bg-[#C5A880] px-4 py-2 font-tech text-[10px] uppercase tracking-[0.16em] text-[#0B1F3B]">{project.status}</div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4 flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.16em] text-[#0B1F3B]/50"><MapPin className="h-4 w-4 text-[#C5A880]" />{project.location}</div>
                    <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0B1F3B]">{project.name}</h2>
                    <p className="mt-2 text-sm italic text-[#0B1F3B]/56">{project.tagline}</p>
                    <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-[#0B1F3B]/65">{project.description}</p>
                    <div className="mt-6 flex items-center justify-between border-t border-[#0B1F3B]/10 pt-5">
                      <div><div className="font-tech text-[10px] uppercase tracking-[0.14em] text-[#0B1F3B]/40">From</div><div className="mt-1 font-semibold text-[#0B1F3B]">{project.pricing.from}</div></div>
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B1F3B] text-white transition-transform group-hover:rotate-45"><ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        <div className="mt-16 rounded-[2rem] bg-[#0B1F3B] p-8 text-[#F5F5F5] sm:p-10" data-testid="projects-cta-panel">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div><div className="mb-3 font-tech text-xs uppercase tracking-[0.24em] text-[#C5A880]">Private allocation</div><h2 className="font-display text-3xl font-semibold">Need a mandate matched to your capital plan?</h2></div>
            <Button onClick={() => { navigate('/'); window.setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 120); }} className="h-12 rounded-full bg-[#C5A880] px-6 font-semibold text-[#0B1F3B] hover:bg-[#EFE6D8]" data-testid="projects-contact-cta">Brief the mandate desk</Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProjectsListing;
